import { entitlementsInfoSchema, stripeAccountNameSchema, type StripeCustomerIdsClaim } from '@inkitt/contracts-common';
import { userTransferHistory } from '@inkitt/titan/schemas/galatea';
import { TransferResult } from '@inkitt/titan/types/UserTransfer';
import { createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { c } from '../../contract';

export const adminRouter = c.router(
  {
    getUserId: {
      summary: 'Get user id by email',
      method: 'GET',
      path: '/get-user-id',
      query: z.object({
        email: z.string().email(),
      }),
      responses: {
        200: z.object({
          userId: z.string(),
        }),
        404: z.literal('User Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
    getUserSignInMethods: {
      summary: 'Get user sign in methods',
      method: 'GET',
      path: '/get-user-sign-in-methods',
      responses: {
        200: z.array(
          z.object({
            id: z.string(),
            primaryEmail: z.string(),
            providerId: z.string(),
            providerEmail: z.string().optional(),
          }),
        ),
        404: z.literal('User Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
    changeEmail: {
      summary: 'Change user email',
      method: 'POST',
      path: '/change-email',
      body: z.object({
        email: z.string().email(),
      }),
      responses: {
        200: z.literal('Success'),
        400: z.literal('Bad Request'),
        500: z.literal('Internal Server Error'),
      },
    },
    resetPassword: {
      summary: 'Send password reset email',
      method: 'POST',
      path: '/reset-password',
      body: z.object({
        email: z.string().email(),
      }),
      responses: {
        200: z.literal('Success'),
        500: z.literal('Internal Server Error'),
      },
    },
    getUserCommentsStories: {
      summary: 'Get user comments',
      method: 'GET',
      path: '/get-user-stories-comments',
      responses: {
        200: z.array(
          z.object({
            id: z.number(),
            body: z.string().nullish(),
            story: z.string().nullish(),
            episode: z.number().nullish(),
          }),
        ),
        404: z.literal('User Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
    getCommentsByFilters: {
      summary: 'Get user comments by filters',
      method: 'GET',
      path: '/get-comments-by-filters',
      query: z.object({
        userId: z.string().optional(),
        story: z.string().optional(),
        episode: z.string().optional(),
        bodyContains: z.string().optional(),
        commentId: z.coerce.number().int().positive().optional(),
        status: z.enum(['allowed', 'toxic', 'blocked']).optional(),
        limit: z.coerce.number().int().positive().optional(),
        sortBy: z.enum(['date', 'likes']).optional(),
      }),
      responses: {
        200: z.array(
          z.object({
            id: z.number(),
            userId: z.string().nullish(),
            body: z.string().nullish(),
            story: z.string().nullish(),
            episode: z.number().nullish(),
            likesCount: z.number().nullish(),
            repliesCount: z.number().nullish(),
            isHidden: z.boolean().nullish(),
            isBlocked: z.boolean().nullish(),
          }),
        ),
        404: z.literal('Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
    getCommentsReportsByFilters: {
      summary: 'Get user comments reports by filters',
      method: 'GET',
      path: '/get-comments-reports-by-filters',
      query: z.object({
        userId: z.string().optional(),
        story: z.string().optional(),
        episode: z.coerce.number().optional(),
        reason: z.enum(['spam', 'inappropriate', 'harmful']).optional(),
        status: z.enum(['pending', 'report-accepted', 'report-rejected']).optional(),
        commentId: z.coerce.number().int().positive().optional(),
        limit: z.coerce.number().int().positive().optional(),
      }),
      responses: {
        200: z.array(
          z.object({
            id: z.number(),
            story: z.string(),
            episode: z.number(),
            reason: z.string(),
            is_processed: z.boolean(),
            status: z.string(),
            created_at: z.date(),
            reporter_user_id: z.string(),
            reported_comment_id: z.number(),
            reported_comment_body: z.string().nullish(),
          }),
        ),
        404: z.literal('Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
    deleteComment: {
      summary: 'Delete comment by id',
      method: 'DELETE',
      path: '/delete-comment',
      body: z.object({
        id: z.number(),
      }),
      responses: {
        200: z.literal('Success'),
        500: z.literal('Internal Server Error'),
      },
    },
    setCommentVisibility: {
      summary: 'Set comment visibility',
      method: 'PATCH',
      path: '/set-comment-visibility',
      body: z.object({
        id: z.number(),
        isVisible: z.boolean(),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    setBulkCommentVisibility: {
      summary: 'Set bulk comment visibility',
      method: 'PATCH',
      path: '/set-bulk-comment-visibility',
      body: z.object({
        ids: z.array(z.number()),
        isVisible: z.boolean(),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    setCommentToxicity: {
      summary: 'Set comment toxicity',
      method: 'PATCH',
      path: '/set-comment-toxicity',
      body: z.object({
        id: z.number(),
        isToxic: z.boolean(),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    setBulkCommentToxicity: {
      summary: 'Set bulk comment toxicity',
      method: 'PATCH',
      path: '/set-bulk-comment-toxicity',
      body: z.object({
        ids: z.array(z.number()),
        isToxic: z.boolean(),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    setCommentReportStatus: {
      summary: 'Set comment report status',
      method: 'PATCH',
      path: '/set-comment-report-status',
      body: z.object({
        id: z.number(),
        status: z.enum(['pending', 'report-accepted', 'report-rejected']),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    setBulkCommentReportStatus: {
      summary: 'Set bulk comment report status',
      method: 'PATCH',
      path: '/set-bulk-comment-report-status',
      body: z.object({
        ids: z.array(z.number()),
        status: z.enum(['pending', 'report-accepted', 'report-rejected']),
      }),
      responses: {
        200: z.object({
          status: z.literal('updated'),
        }),
        400: z.object({
          status: z.literal('rejected'),
          reason: z.string(),
        }),
        500: z.literal('Internal Server Error'),
      },
    },
    deleteFamilyPlanMember: {
      summary: 'Delete family plan member',
      method: 'DELETE',
      path: '/family-plan/member',
      body: z.object({
        userId: z.string().min(1),
      }),
      responses: {
        200: z.boolean(),
      },
    },

    getFamilyPlan: {
      summary: 'Get family plan',
      method: 'GET',
      path: '/family-plan',
      query: z.object({
        managerUserId: z.string().optional(),
        memberUserId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          planDetails: z.object({
            userId: z.string(),
            familyPlanCountry: z.string(),
            limit: z.number(),
            familyJoinCode: z.string(),
          }),
          members: z.array(
            z.object({
              displayName: z.string(),
              avatar: z.string().optional(),
              userId: z.string(),
            }),
          ),
        }),
      },
    },
    transferUser: {
      summary: 'Transfer user',
      method: 'POST',
      path: '/transfer',
      body: z.object({
        sourceUserId: z.string().min(1),
        targetUserId: z.string().min(1),
        /**
         * Identifies which brand wallet the points transfer operates on (e.g. Audiobooks Plus ids —
         * its coins live in a separate RTDB instance + ledger tables). Absent = Galatea's wallet
         * (backward compatible). Unknown values are rejected by the server.
         */
        applicationBundleId: z.string().min(1).optional(),
      }),
      responses: {
        200: z.object({
          result: TransferResult,
        }),
      },
    },
    getUserTransferHistory: {
      summary: 'Get user transfer history',
      method: 'GET',
      path: '/transfer',
      query: z.object({
        userId: z.string().min(1),
      }),
      responses: {
        200: z.array(
          createSelectSchema(userTransferHistory).extend({
            result: TransferResult.nullish(),
          }),
        ),
      },
    },
    getUserData: {
      summary: 'Get user data',
      method: 'GET',
      path: '/user-data',
      query: z.object({
        userId: z.string().min(1),
        /** Brand wallet selector for the coins balance — see `transferUser.body.applicationBundleId`. */
        applicationBundleId: z.string().min(1).optional(),
      }),
      responses: {
        200: z.any(),
      },
    },
    getUserPointsLedger: {
      summary: 'Get user points ledger',
      method: 'GET',
      path: '/user-points-ledger',
      query: z.object({
        userId: z.string().min(1),
        /** Brand wallet selector for the ledger — see `transferUser.body.applicationBundleId`. */
        applicationBundleId: z.string().min(1).optional(),
      }),
      responses: {
        200: z.any(),
      },
    },
    getUserSubscriptions: {
      summary: 'Get user subscriptions',
      method: 'GET',
      path: '/subscriptions',
      query: z.object({
        userId: z.string().min(1),
        callSource: z.string(),
        stripeCustomerId: z.string().optional(),
        bypassCache: z.coerce.boolean().optional(),
      }),
      responses: {
        200: entitlementsInfoSchema,
      },
    },
    getEntitlementsFromTethys: {
      summary: 'Get entitlements from Tethys',
      method: 'GET',
      path: '/tethys-entitlements',
      query: z.object({
        userId: z.string().min(1),
        callSource: z.string(),
      }),
      responses: {
        // NOTE: we don't strictly type the response on purpose because this endpoint is *only* designed to be consumed by internal services (analytics, recs engine, etc.)
        200: c.type<unknown>(),
        404: z.literal('User not found'),
      },
    },
    mintCustomToken: {
      summary: 'Mint a Firebase custom token for the given user id, cached for 50 min',
      method: 'POST',
      path: '/mint-custom-token',
      body: z.object({
        userId: z.string().min(1),
      }),
      responses: {
        200: z.object({
          token: z.string(),
        }),
        // On failure the Nest default exception filter emits its own 500 shape;
        // keep this loose so the contract doesn't diverge from runtime behavior.
        500: c.type<unknown>(),
      },
    },
    updateStripeCustomerClaim: {
      summary: 'Set the galatea Stripe customer id for the given account in the user Firebase custom claims',
      method: 'POST',
      path: '/update-stripe-customer-claim',
      body: z.object({
        userId: z.string(),
        stripeCustomerId: z.string(),
        stripeAccount: stripeAccountNameSchema,
      }),
      responses: {
        200: c.type<StripeCustomerIdsClaim>(),
        404: z.literal('User Not Found'),
        500: z.literal('Internal Server Error'),
      },
    },
  },
  {
    pathPrefix: '/admin',
  },
);
